import { skipToken } from "@reduxjs/toolkit/query/react";
import { useTenant } from "hooks/settings";
import { Loaded } from "models/loaded";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  OptimisationFailureReason,
  Plan,
  useGetResultQuery,
  useRunSearchMutation,
} from "src/store/api/generatedApi";

type PollingStatus = "ready" | "queueing" | "polling";

const pollingIntervals: Record<"ready" | "polling", number> = {
  ready: 10000,
  polling: 2000,
};

export interface SyncedResultsState {
  run: (searchId: number) => void;
  isStale: boolean;
  results: Loaded<{
    plans: Plan[];
    errors: OptimisationFailureReason[];
  } | null>;
}

export const useSyncedResultsState = (
  clientSearchId: number | null,
  revision: number,
  isStale: boolean,
  setIsStale: (isStale: boolean) => void
): SyncedResultsState => {
  const [runSearch] = useRunSearchMutation();
  const tenant = useTenant();

  // Identifier for the search whose result is currently loaded.  Note that
  // this may not match the search whose *parameters* are currently loaded
  const [resultsSearchId, setResultsSearchId] = useState(clientSearchId);
  const [status, setStatus] = useState<PollingStatus>("ready");

  useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets result ID when search changes
    setResultsSearchId(clientSearchId);
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets polling status when search changes
    setStatus("ready");
    setIsStale(false);
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- setIsStale is a prop function, not a stable useState setter; adding it causes unnecessary effect re-runs
  }, [clientSearchId, revision]);

  // Always poll when viewing a search to catch updates from other pages/tabs
  const shouldPoll = resultsSearchId != null && status !== "queueing";

  const { data, isFetching, isError } = useGetResultQuery(
    resultsSearchId == null || status === "queueing"
      ? skipToken
      : { tenantName: tenant, searchId: resultsSearchId },
    shouldPoll ? { pollingInterval: pollingIntervals[status] } : {}
  );

  useEffect(() => {
    if (
      !isFetching &&
      status === "polling" &&
      (isError || (data != null && data.status !== "incomplete"))
    ) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- polling complete, transition to ready
      setStatus("ready");
    }

    /* Loading up an in-progress optimisation will typically result in an
      invalid initial state; so if the result comes back from the server saying
      that it's still being optimised, trust it, and force the state */
    if (data?.status === "incomplete" && status !== "polling") {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- server reports incomplete, force polling state
      setStatus("polling");
    }
  }, [data, isFetching, status, isError]);

  const loadedResult = data?.search_id !== resultsSearchId ? null : data;

  const run = useCallback(
    (runSearchId: number) => {
      setResultsSearchId(runSearchId);
      setIsStale(false);
      setStatus("queueing");

      void runSearch({
        tenantName: tenant,
        searchId: runSearchId,
        runSearchOptions: {},
      })
        .unwrap()
        .then(() =>
          // Don't override other optimisations if the active search has changed
          // in the meantime
          {
            setStatus((current) =>
              current === "queueing" ? "polling" : current
            );
          }
        );
    },
    [runSearch, setIsStale, tenant]
  );

  const results: Loaded<{
    plans: Plan[];
    errors: OptimisationFailureReason[];
  } | null> = useMemo(() => {
    if (isError) {
      return { status: "error" };
    }
    if (status === "queueing" || loadedResult == null) {
      return { status: "loading" };
    }
    return {
      status: "success",
      data:
        loadedResult.status === "not_started"
          ? null
          : {
              plans: loadedResult.plans,
              errors:
                loadedResult.failure_reason == null
                  ? []
                  : [loadedResult.failure_reason],
            },
      fetching: status === "polling",
    };
  }, [isError, loadedResult, status]);

  const staleResults =
    isStale && loadedResult != null && loadedResult.status !== "not_started";

  return useMemo(
    () => ({ run, isStale: staleResults, results }),
    [run, staleResults, results]
  );
};
